import {
  DocumentSighted,
  PrimaryProofOfIdentityDocumentSighted,
  ProofOfIdentity,
  ProofOfLegalOwnershipDocumentSighted,
  UploadedDocumentDocumentType,
  VendorModel,
} from '@urbanx/agx.campaigns.client';
import { useSelectedCampaign } from './useSelectedCampaign';
import { useMemo } from 'react';
import { ProofOfIdentity as OldProofOfIdentity } from '@urbanx/agx-ui-components';

const mapProofOfIdentity = (
  old: OldProofOfIdentity | undefined
): ProofOfIdentity | undefined => {
  if (!old) return undefined;

  return {
    ...old,
    primaryProofOfIdentity: !old.primaryProofOfIdentity
      ? undefined
      : {
          ...old.primaryProofOfIdentity,
          documentSighted: old.primaryProofOfIdentity
            .documentSighted as unknown as PrimaryProofOfIdentityDocumentSighted,
          dateOfIssue: !old.primaryProofOfIdentity.dateOfIssue
            ? undefined
            : new Date(old.primaryProofOfIdentity.dateOfIssue),
          expiryDate: !old.primaryProofOfIdentity.expiryDate
            ? undefined
            : new Date(old.primaryProofOfIdentity.expiryDate),
        },
    secondaryProofOfIdentity: !old.secondaryProofOfIdentity
      ? undefined
      : {
          ...old.secondaryProofOfIdentity,
          documentSighted: old.secondaryProofOfIdentity
            .documentSighted as unknown as DocumentSighted[],
        },
    proofOfLegalOwnership: !old.proofOfLegalOwnership
      ? undefined
      : {
          ...old.proofOfLegalOwnership,
          documentSighted: old.proofOfLegalOwnership
            .documentSighted as unknown as ProofOfLegalOwnershipDocumentSighted,
        },
  };
};

export const useSelectedCampaignVendors = () => {
  const { selectedCampaign } = useSelectedCampaign();

  const vendors: VendorModel[] = useMemo(() => {
    if (!selectedCampaign) return [];

    return (
      selectedCampaign.vendors?.map(vendor => {
        return {
          ...vendor,
          legalName: vendor.legalName ?? undefined,
          individual: !vendor.individual
            ? undefined
            : {
                ...vendor.individual,
                proofOfIdentity: mapProofOfIdentity(
                  vendor.individual.proofOfIdentity
                ),
              },
          company: !vendor.company
            ? undefined
            : {
                ...vendor.company,
                signatories: vendor.company.signatories.map(signatory => {
                  return {
                    ...signatory,
                    proofOfIdentity: mapProofOfIdentity(
                      signatory.proofOfIdentity
                    ),
                  };
                }),
              },
          deceasedEstate: !vendor.deceasedEstate
            ? undefined
            : {
                ...vendor.deceasedEstate,
                executors: vendor.deceasedEstate.executors.map(executor => {
                  return {
                    ...executor,
                    proofOfIdentity: mapProofOfIdentity(
                      executor.proofOfIdentity
                    ),
                  };
                }),
              },
          powerOfAttorney: !vendor.powerOfAttorney
            ? undefined
            : {
                ...vendor.powerOfAttorney,
                documentation: !vendor.powerOfAttorney.documentation
                  ? undefined
                  : {
                      ...vendor.powerOfAttorney.documentation,
                      documentType: vendor.powerOfAttorney.documentation
                        .documentType as unknown as UploadedDocumentDocumentType,
                    },
                attorneys:
                  vendor.powerOfAttorney.attorneys.map(attorney => {
                    return {
                      ...attorney,
                      proofOfIdentity: mapProofOfIdentity(
                        attorney.proofOfIdentity
                      ),
                    };
                  }) ?? [],
              },
        };
      }) ?? []
    );
  }, [selectedCampaign]);

  return vendors;
};
